import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useApp, getAllTasks, formatActivityHeadline, getLatestActivityByProject, LIVE_HEADLINE_MS,
} from '../context/AppContext';
import AppLayout from '../components/layout/AppLayout';
import { Button, Modal, Input, Textarea, EmptyState, ConfirmToast, Toast, Select } from '../components/ui';
import { PROJECT_COLORS, STATUSES, PRIORITIES } from '../data/constants';

const relativeTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return 'just now';
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/** Animate grid items to new positions when sort order changes (FLIP). */
function useFlipReorder(orderKey) {
  const containerRef = useRef(null);
  const prevPositions = useRef(new Map());

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const nodes = container.querySelectorAll('[data-flip-id]');
    const nextPositions = new Map();
    nodes.forEach((node) => {
      nextPositions.set(node.getAttribute('data-flip-id'), node.getBoundingClientRect());
    });

    nodes.forEach((node) => {
      const id = node.getAttribute('data-flip-id');
      const prev = prevPositions.current.get(id);
      const next = nextPositions.get(id);
      if (!prev || !next) return;
      const dx = prev.left - next.left;
      const dy = prev.top - next.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      node.style.transform = `translate(${dx}px, ${dy}px)`;
      node.style.transition = 'transform 0s';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          node.style.transition = 'transform 0.35s ease';
          node.style.transform = '';
        });
      });
    });

    prevPositions.current = nextPositions;
  }, [orderKey]);

  return containerRef;
}

/* ── ProjectCard ──────────────────────────────────────── */
function ProjectCard({
  project, onClick, onDelete, newCount = 0, hasStatusUpdate = false,
  isRecentlyChanged = false, liveActivity = null,
}) {
  const [hover, setHover] = useState(false);
  const allTasks = getAllTasks(project);
  const done  = allTasks.filter(t => t.status === 'done').length;
  const total = allTasks.length;
  const pct   = total ? Math.round((done / total) * 100) : 0;
  const subCount = project.children?.length ?? 0;
  const headline = liveActivity ? formatActivityHeadline(liveActivity) : '';
  const isLive = !!liveActivity || isRecentlyChanged;
  const showUpdatedChip = hasStatusUpdate || liveActivity?.type === 'task_updated';

  const cardClass = [
    isRecentlyChanged ? 'pulse-highlight' : '',
    isLive ? 'project-card--live' : '',
  ].filter(Boolean).join(' ') || undefined;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={cardClass}
      style={{
        background: '#fff', borderRadius: 20, padding: '1.5rem',
        border: newCount > 0
          ? '1.5px solid #fca5a5'
          : showUpdatedChip
            ? '1.5px solid #fcd34d'
            : (hover ? `1.5px solid ${project.color}55` : '1.5px solid #f1f5f9'),
        cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s',
        boxShadow: hover && !isLive ? `0 12px 36px rgba(0,0,0,0.1)` : (isLive ? undefined : '0 1px 3px rgba(0,0,0,0.04)'),
        transform: hover ? 'translateY(-3px)' : 'none',
        position: 'relative',
      }}
    >
      {/* Delete button */}
      <button
        onClick={e => { e.stopPropagation(); onDelete(); }}
        style={{
          position: 'absolute', top: 12, right: 12,
          width: 26, height: 26, borderRadius: 8, border: '1.5px solid #fee2e2',
          background: '#fff', color: '#fca5a5', fontSize: 14,
          display: hover ? 'flex' : 'none', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#ef4444'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#fca5a5'; }}
      >×</button>

      {/* Icon */}
      <div style={{
        width: 48, height: 48, borderRadius: 14, marginBottom: '1rem',
        background: project.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ width: 20, height: 20, borderRadius: 6, background: project.color, opacity: 0.9 }} />
      </div>

      {(newCount > 0 || showUpdatedChip) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 2 }}>
          {newCount > 0 && (
            <span className="project-card__chip project-card__chip--new">
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }} />
              {newCount} new
            </span>
          )}
          {showUpdatedChip && (
            <span className="project-card__chip project-card__chip--updated">
              <span className="ping-dot" style={{ color: '#f59e0b', width: 6, height: 6 }} />
              Updated
            </span>
          )}
        </div>
      )}

      {headline && (
        <div className="project-card__live-headline">
          <span className="ping-dot" style={{ color: '#6C63FF', marginTop: 3 }} />
          <span>{headline}</span>
        </div>
      )}

      <h3 style={{ fontSize: 17, fontWeight: 800, color: '#0f0f1a', marginBottom: 6, letterSpacing: '-0.02em', paddingRight: 24 }}>
        {project.title}
      </h3>
      {subCount > 0 && (
        <span style={{
          display: 'inline-block', fontSize: 11, fontWeight: 600,
          color: project.color, background: project.color + '15',
          padding: '3px 8px', borderRadius: 6, marginBottom: 8,
        }}>
          {subCount} subproject{subCount !== 1 ? 's' : ''}
        </span>
      )}
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: '1.25rem', lineHeight: 1.5 }}>
        {project.description || 'No description'}
      </p>

      {/* Progress bar */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>Progress</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: project.color }}>{pct}%</span>
        </div>
        <div style={{ height: 6, background: '#f1f5f9', borderRadius: 99 }}>
          <div style={{
            height: '100%', width: `${pct}%`, borderRadius: 99,
            background: `linear-gradient(90deg, ${project.color}, ${project.color}99)`,
            transition: 'width 0.6s ease',
          }} />
        </div>
      </div>

      {/* Status chips */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {STATUSES.map(s => {
          const count = allTasks.filter(t => t.status === s.id).length;
          return count > 0 ? (
            <span key={s.id} style={{
              fontSize: 11, padding: '3px 8px', borderRadius: 6,
              background: s.bg, color: s.color, fontWeight: 600,
            }}>
              {count} {s.label}
            </span>
          ) : null;
        })}
        {total === 0 && (
          <span style={{ fontSize: 11, color: '#cbd5e1' }}>No tasks yet</span>
        )}
      </div>
    </div>
  );
}

/* ── LiveActivityStrip ────────────────────────────────── */
function LiveActivityStrip({ items, onOpenProject }) {
  if (!items.length) return null;
  return (
    <div className="live-activity-strip">
      <div className="live-activity-strip__header">
        <span className="ping-dot" style={{ color: '#6C63FF' }} />
        <span className="live-activity-strip__title">Live activity</span>
      </div>
      <div className="live-activity-strip__list">
        {items.map(a => (
          <button
            key={a.id}
            type="button"
            className="live-activity-strip__item"
            onClick={() => onOpenProject(a.rootProjectId)}
          >
            <span style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              background: a.projectColor || '#6C63FF',
            }} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
              {formatActivityHeadline(a)}
              <span style={{ fontWeight: 500, color: '#94a3b8' }}> · {a.projectTitle}</span>
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{relativeTime(a.timestamp)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── CreateTaskModal ──────────────────────────────────── */
function buildProjectOptions(projects) {
  const options = [];
  for (const p of projects) {
    options.push({ value: p.id, label: p.title, color: p.color });
    for (const child of p.children ?? []) {
      options.push({ value: child.id, label: `${p.title} / ${child.title}`, color: child.color || p.color });
    }
  }
  return options;
}

function CreateTaskModal({ projects, onClose, onCreate }) {
  const projectOptions = buildProjectOptions(projects);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [status, setStatus] = useState('todo');
  const [priority, setPriority] = useState('medium');
  const [projectId, setProjectId] = useState(projectOptions[0]?.value || '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const selectedColor = projectOptions.find(o => o.value === projectId)?.color || '#6C63FF';

  const handleCreate = async () => {
    if (!title.trim()) { setError('Task title is required.'); return; }
    if (!projectId) { setError('Select a project.'); return; }
    setSubmitting(true);
    try {
      await onCreate(projectId, { title: title.trim(), desc: desc.trim(), status, priority });
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not create task.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Create Task" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <Input
          label="Task title"
          required
          value={title}
          onChange={e => { setTitle(e.target.value); setError(''); }}
          placeholder="What needs to be done?"
        />
        <Textarea
          label="Description"
          value={desc}
          onChange={e => setDesc(e.target.value)}
          placeholder="Additional details…"
          rows={2}
        />
        <Select
          label="Project"
          required
          value={projectId}
          onChange={e => { setProjectId(e.target.value); setError(''); }}
          options={projectOptions.length > 0
            ? projectOptions.map(o => ({ value: o.value, label: o.label }))
            : [{ value: '', label: 'No projects available' }]}
        />
        <div className="form-grid-2">
          <Select
            label="Status"
            value={status}
            onChange={e => setStatus(e.target.value)}
            options={STATUSES.map(s => ({ value: s.id, label: s.label }))}
          />
          <Select
            label="Priority"
            value={priority}
            onChange={e => setPriority(e.target.value)}
            options={Object.entries(PRIORITIES).map(([k, v]) => ({ value: k, label: v.label }))}
          />
        </div>
        {error && (
          <p style={{ fontSize: 13, color: '#ef4444', background: '#fee2e2', padding: '8px 12px', borderRadius: 8 }}>{error}</p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={submitting || projectOptions.length === 0}
            style={{ background: selectedColor, boxShadow: `0 2px 8px ${selectedColor}44` }}
          >
            {submitting ? 'Creating…' : 'Create task'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ── CreateProjectModal ───────────────────────────────── */
function CreateProjectModal({ onClose, onCreate }) {
  const [title, setTitle]       = useState('');
  const [desc, setDesc]         = useState('');
  const [color, setColor]       = useState(PROJECT_COLORS[0]);
  const [error, setError]       = useState('');

  const handleCreate = () => {
    if (!title.trim()) { setError('Project name is required.'); return; }
    onCreate({ title: title.trim(), description: desc.trim(), color });
    onClose();
  };

  return (
    <Modal title="New Project" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        <Input
          label="Project name"
          required
          value={title}
          onChange={e => { setTitle(e.target.value); setError(''); }}
          placeholder="e.g. Website Redesign"
        />
        <Textarea
          label="Description"
          value={desc}
          onChange={e => setDesc(e.target.value)}
          placeholder="What is this project about?"
          rows={2}
        />

        {/* Color picker */}
        <div>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 10 }}>
            Color
          </label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {PROJECT_COLORS.map(c => (
              <button
                key={c}
                onClick={() => setColor(c)}
                style={{
                  width: 32, height: 32, borderRadius: '50%', background: c, border: 'none',
                  cursor: 'pointer', transition: 'all 0.15s',
                  outline: color === c ? `3px solid ${c}` : 'none',
                  outlineOffset: 2, transform: color === c ? 'scale(1.15)' : 'none',
                }}
              />
            ))}
          </div>
        </div>

        {error && (
          <p style={{ fontSize: 13, color: '#ef4444', background: '#fee2e2', padding: '8px 12px', borderRadius: 8 }}>
            {error}
          </p>
        )}

        {/* Preview */}
        <div style={{
          background: '#f8fafc', borderRadius: 12, padding: '1rem',
          border: `1.5px solid ${color}44`, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 18, height: 18, borderRadius: 5, background: color }} />
          </div>
          <div>
            <p style={{ fontSize: 14, fontWeight: 700, color: '#0f0f1a' }}>{title || 'Project name'}</p>
            <p style={{ fontSize: 12, color: '#94a3b8' }}>{desc || 'No description'}</p>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleCreate}>Create project</Button>
        </div>
      </div>
    </Modal>
  );
}

/* ── DashboardPage ────────────────────────────────────── */
export default function DashboardPage() {
  const {
    projects, addProject, deleteProject, addTask,
    notificationsMap, statusUpdatesMap, activityMap,
    recentlyChangedProjects, activityLog,
  } = useApp();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  // Refresh "freshness" so live headlines expire without waiting for a poll.
  useEffect(() => {
    if (!activityLog.length) return undefined;
    const id = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(id);
  }, [activityLog.length]);

  const totalTasks    = projects.reduce((s, p) => s + getAllTasks(p).length, 0);
  const doneTasks     = projects.reduce((s, p) => s + getAllTasks(p).filter(t => t.status === 'done').length, 0);
  const inProgTasks   = projects.reduce((s, p) => s + getAllTasks(p).filter(t => t.status === 'inprogress').length, 0);

  const newCountFor = (p) => notificationsMap[p.id] || 0;

  const activityFor = (p) => activityMap[p.id] || 0;
  const sortedProjects = [...projects].sort((a, b) => activityFor(b) - activityFor(a));
  const orderKey = sortedProjects.map(p => p.id).join(',');
  const flipRef = useFlipReorder(orderKey);

  const latestByProject = useMemo(
    () => getLatestActivityByProject(activityLog, LIVE_HEADLINE_MS, now),
    [activityLog, now],
  );

  const liveStripItems = useMemo(() => {
    return (activityLog || [])
      .filter(a => {
        const ts = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        return ts && now - ts <= LIVE_HEADLINE_MS;
      })
      .slice(0, 5);
  }, [activityLog, now]);

  const handleCreate = async (data) => {
    try {
      const id = await addProject(data);
      navigate(`/project/${id}`);
    } catch (e) {
      setToast({ type: 'error', message: e?.message || 'Could not create project.' });
    }
  };

  const handleCreateTask = async (projectId, taskData) => {
    await addTask(projectId, taskData);
    setToast({ type: 'success', message: `Task "${taskData.title}" added.` });
    navigate(`/project/${projectId}`);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteProject(pendingDelete.id);
      const subCount = pendingDelete.children?.length ?? 0;
      const extra = subCount > 0 ? ` and ${subCount} subproject${subCount !== 1 ? 's' : ''}` : '';
      setToast({ type: 'success', message: `"${pendingDelete.title}"${extra} removed.` });
      setPendingDelete(null);
    } catch (e) {
      setToast({ type: 'error', message: e?.message || 'Could not delete project.' });
    } finally {
      setDeleting(false);
    }
  };

  const deleteMessage = pendingDelete
    ? (() => {
        const subCount = pendingDelete.children?.length ?? 0;
        const subNote = subCount > 0
          ? ` This will also delete ${subCount} subproject${subCount !== 1 ? 's' : ''}.`
          : '';
        return `Are you sure you want to delete "${pendingDelete.title}"?${subNote} This cannot be undone.`;
      })()
    : '';

  return (
    <AppLayout>
      <div className="page-content">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: 30, fontWeight: 800, color: '#0f0f1a', letterSpacing: '-0.03em', marginBottom: 4 }}>
              Tasks
            </h1>
            <p style={{ color: '#64748b', fontSize: 15 }}>
              {projects.length} project{projects.length !== 1 ? 's' : ''} · {totalTasks} tasks total
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              onClick={() => setShowCreateTask(true)}
              size="md"
              disabled={projects.length === 0}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
              Create Task
            </Button>
            <Button variant="primary" onClick={() => setShowCreate(true)} size="md">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
              New Project
            </Button>
          </div>
        </div>

        <LiveActivityStrip
          items={liveStripItems}
          onOpenProject={(id) => navigate(`/project/${id}`)}
        />

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
          {[
            { label: 'Projects',    value: projects.length, emoji: '🗂️', color: '#6C63FF', path: null },
            { label: 'Total Tasks', value: totalTasks,      emoji: '📋', color: '#3b82f6', path: '/tasks/all' },
            { label: 'Completed',   value: doneTasks,       emoji: '✅', color: '#10b981', path: '/tasks/done' },
            { label: 'In Progress', value: inProgTasks,     emoji: '⚡', color: '#f59e0b', path: '/tasks/inprogress' },
          ].map((s, i) => {
            const clickable = !!s.path;
            const CardTag = clickable ? 'button' : 'div';
            return (
              <CardTag
                key={s.label}
                type={clickable ? 'button' : undefined}
                onClick={clickable ? () => navigate(s.path) : undefined}
                style={{
                  background: '#fff', borderRadius: 16, padding: '1.25rem 1.5rem',
                  border: '1.5px solid #f1f5f9',
                  animation: `fadeInUp 0.3s ease ${i * 50}ms both`,
                  textAlign: 'left',
                  cursor: clickable ? 'pointer' : 'default',
                  transition: clickable ? 'all 0.15s' : undefined,
                  width: '100%',
                }}
                onMouseEnter={clickable ? e => {
                  e.currentTarget.style.borderColor = s.color + '55';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                } : undefined}
                onMouseLeave={clickable ? e => {
                  e.currentTarget.style.borderColor = '#f1f5f9';
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                } : undefined}
              >
                <div style={{ fontSize: 26, marginBottom: 10 }}>{s.emoji}</div>
                <div style={{ fontSize: 30, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>{s.label}</div>
                {clickable && (
                  <div style={{ fontSize: 11, color: s.color, marginTop: 8, fontWeight: 600, opacity: 0.85 }}>
                    View list →
                  </div>
                )}
              </CardTag>
            );
          })}
        </div>

        {/* Projects grid */}
        {projects.length === 0 ? (
          <EmptyState
            icon="🚀"
            title="No projects yet"
            desc="Create your first project to start organizing tasks and notes."
            action={<Button variant="primary" onClick={() => setShowCreate(true)}>Create your first project</Button>}
          />
        ) : (
          <div ref={flipRef} className="projects-grid-flip">
            {sortedProjects.map((p) => (
              <div key={p.id} data-flip-id={p.id} className="projects-grid-flip__item">
                <ProjectCard
                  project={p}
                  newCount={newCountFor(p)}
                  hasStatusUpdate={!!statusUpdatesMap[p.id]}
                  isRecentlyChanged={recentlyChangedProjects.has(p.id)}
                  liveActivity={latestByProject[p.id] || null}
                  onClick={() => navigate(`/project/${p.id}`)}
                  onDelete={() => setPendingDelete(p)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateProjectModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />
      )}

      {showCreateTask && (
        <CreateTaskModal
          projects={projects}
          onClose={() => setShowCreateTask(false)}
          onCreate={handleCreateTask}
        />
      )}

      <ConfirmToast
        open={!!pendingDelete}
        title="Delete project?"
        message={deleteMessage}
        confirmLabel="Yes, delete"
        cancelLabel="No, cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => !deleting && setPendingDelete(null)}
        loading={deleting}
      />

      <Toast
        message={toast?.message}
        type={toast?.type}
        onClose={() => setToast(null)}
      />
    </AppLayout>
  );
}
